import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import DOMPurify from "dompurify";
import type { GatewayConnectionState, ChatMessage, ConversationMode, SubAgentRun } from "../services/index.ts";
import { PLATFORM_LIST, PLATFORMS } from "../components/platform-icons.ts";
import { TIERS, getModelForTier, getModelById, loadToolPreferences } from "../models.ts";
import { SpeechRecognitionService, type SpeechRecognitionState } from "../services/speech-recognition.ts";
import "../components/canvas-panel.ts";

/** Slash command exposed by an installed skill */
export type SkillSlashCommand = {
  skillName: string;
  skillEmoji?: string;
  name: string;
  description: string;
  prepend_prompt: string;
};

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: "var(--channel-whatsapp)",
  telegram: "var(--channel-telegram)",
  discord: "var(--channel-discord)",
  slack: "var(--channel-slack)",
  signal: "var(--channel-signal)",
  imessage: "var(--channel-imessage)",
  matrix: "var(--channel-matrix)",
  teams: "var(--channel-teams)",
};

// ── Markdown rendering pipeline ──
marked.setOptions({ gfm: true, breaks: true });

// Force external links to open in a new tab safely
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "del", "code", "pre",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "blockquote",
    "table", "thead", "tbody", "tr", "th", "td",
    "hr", "a", "img", "span",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "target", "rel"],
};

/** Regex to detect TTS audio data URIs embedded in assistant messages */
const AUDIO_DATA_URI_RE = /data:audio\/mpeg;base64,[A-Za-z0-9+/=]+/g;

function renderMarkdown(content: string): string {
  const raw = marked.parse(content) as string;
  return DOMPurify.sanitize(raw, PURIFY_CONFIG);
}

/**
 * Extract audio data URIs from message content and return them separately.
 * Returns the cleaned content (without data URIs) and an array of audio URLs.
 */
function extractAudioUrls(content: string): { cleanContent: string; audioUrls: string[] } {
  const audioUrls: string[] = [];
  const cleanContent = content.replace(AUDIO_DATA_URI_RE, (match) => {
    audioUrls.push(match);
    return "";
  }).trim();
  return { cleanContent, audioUrls };
}

const IMAGE_DATA_URI_RE = /data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/=]+/g;
/** Marker format used by the gateway for signed screenshot URLs */
const SCREENSHOT_MARKER_RE = /\{\{SCREENSHOT:(https?:\/\/[^}]+)\}\}/g;

/**
 * Extract image URLs from message content and return them separately.
 * Handles both inline base64 data URIs (streaming fallback) and
 * {{SCREENSHOT:url}} markers (signed URLs from storage).
 */
function extractImageUrls(content: string): { cleanContent: string; imageUrls: string[] } {
  const imageUrls: string[] = [];
  let cleanContent = content.replace(IMAGE_DATA_URI_RE, (match) => {
    imageUrls.push(match);
    return "";
  });
  cleanContent = cleanContent.replace(SCREENSHOT_MARKER_RE, (_match, url) => {
    imageUrls.push(url);
    return "";
  });
  return { cleanContent: cleanContent.trim(), imageUrls };
}

// Platform icons imported from shared module

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getDateLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 86400000 && d.getDate() === now.getDate()) return "Today";
  if (diff < 172800000) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

@customElement("katanoid-chat-view")
export class KatanoidChatView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: Array }) messages: ChatMessage[] = [];
  @property({ type: Boolean }) isTyping = false;
  @property({ type: String }) gatewayState: GatewayConnectionState = "disconnected";
  @property({ type: String }) assistantName = "Katanoid";
  @property({ type: String }) streamingMessageId: string | null = null;
  @property({ type: String }) streamingContent = "";
  @property({ type: String }) conversationMode: ConversationMode = "normal";
  @property({ type: Array }) activeTools: string[] = [];
  @property({ type: String }) toolStatus: "idle" | "running" | "done" = "idle";
  @property({ type: Boolean }) memoryEnabled = false;
  @property({ type: Array }) skillCommands: SkillSlashCommand[] = [];
  @property({ type: Array }) subAgentRuns: SubAgentRun[] = [];
  @property({ type: Boolean }) forkMode = false;
  @property({ type: Boolean }) canvasVisible = false;

  @state() private inputValue = "";
  @state() private modePopoverOpen = false;
  @state() private commandPickerOpen = false;
  @state() private commandPickerIndex = 0;
  @state() private pendingImage: string | null = null;
  @state() private lightboxUrl: string | null = null;
  @state() private selectionText = "";
  @state() private selectionPos: { x: number; y: number } | null = null;
  @state() private memorySaveStatus: "idle" | "saving" | "saved" = "idle";
  @state() private voiceState: SpeechRecognitionState = "idle";
  @state() private interimTranscript = "";
  private speechService: SpeechRecognitionService | null = null;
  @query(".chat-messages") private messagesContainer?: HTMLElement;

  private _onDocumentClick = (e: MouseEvent) => {
    if (this.modePopoverOpen) {
      const anchor = this.querySelector(".chat-mode-anchor");
      if (anchor && !anchor.contains(e.target as Node)) {
        this.modePopoverOpen = false;
      }
    }
  };

  private _onSelectionChange = () => {
    if (!this.memoryEnabled) return;

    const sel = window.getSelection();
    const text = sel?.toString().trim() ?? "";

    if (text.length < 3 || !sel?.rangeCount) {
      // Only clear if not in "saved" state (keep showing the checkmark briefly)
      if (this.memorySaveStatus !== "saved") {
        this.selectionPos = null;
        this.selectionText = "";
      }
      return;
    }

    // Check that the selection is inside a .message-card within this component
    const anchor = sel.anchorNode;
    const card = anchor instanceof Element
      ? anchor.closest(".message-card")
      : anchor?.parentElement?.closest(".message-card");
    if (!card || !this.contains(card)) {
      this.selectionPos = null;
      this.selectionText = "";
      return;
    }

    // Position the button above the selection
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const container = this.messagesContainer;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    this.selectionText = text;
    this.selectionPos = {
      x: rect.left + rect.width / 2 - containerRect.left,
      y: rect.top - containerRect.top + container.scrollTop - 8,
    };
    // Reset save status for new selection
    if (this.memorySaveStatus === "saved") {
      this.memorySaveStatus = "idle";
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this._onDocumentClick);
    document.addEventListener("selectionchange", this._onSelectionChange);
    document.addEventListener("keydown", this._onLightboxKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this._onDocumentClick);
    document.removeEventListener("selectionchange", this._onSelectionChange);
    document.removeEventListener("keydown", this._onLightboxKey);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has("messages") || changed.has("isTyping") || changed.has("streamingContent") || changed.has("toolStatus")) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      if (this.messagesContainer) {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
      }
    });
  }

  // ── Save to Memory ──

  private handleSaveToMemory(e: MouseEvent): void {
    e.preventDefault(); // Keep selection alive (mousedown, not click)
    if (!this.selectionText || this.memorySaveStatus !== "idle") return;

    this.memorySaveStatus = "saving";
    this.dispatchEvent(
      new CustomEvent("save-to-memory", {
        detail: { text: this.selectionText },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Called by consumer-app after save succeeds */
  public memorySaved(): void {
    this.memorySaveStatus = "saved";
    setTimeout(() => {
      this.memorySaveStatus = "idle";
      this.selectionPos = null;
      this.selectionText = "";
    }, 1500);
  }

  /** Called by consumer-app after save fails */
  public memorySaveFailed(error: string): void {
    console.error("[Chat] Failed to save to memory:", error);
    this.memorySaveStatus = "idle";
  }

  // ── Screenshot lightbox ──

  private openLightbox(url: string): void {
    this.lightboxUrl = url;
  }

  private closeLightbox(): void {
    this.lightboxUrl = null;
  }

  private _onLightboxKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.closeLightbox();
  };

  private renderLightbox(): TemplateResult | string {
    if (!this.lightboxUrl) return "";
    return html`
      <div
        class="screenshot-lightbox"
        @click=${this.closeLightbox}
        @keydown=${this._onLightboxKey}
      >
        <img src=${this.lightboxUrl} alt="Screenshot" @click=${(e: Event) => e.stopPropagation()} />
        <button class="screenshot-lightbox-close" @click=${this.closeLightbox}>&times;</button>
      </div>
    `;
  }

  private renderCommandPicker(): TemplateResult {
    const filtered = this.getFilteredCommands();
    if (filtered.length === 0) {
      return html`
        <div class="chat-command-picker">
          <div class="skills-empty" style="padding: var(--space-4);">No matching commands</div>
        </div>
      `;
    }
    return html`
      <div class="chat-command-picker">
        ${filtered.map(
          (cmd, i) => html`
            <button
              class="chat-command-item ${i === this.commandPickerIndex ? "chat-command-item--active" : ""}"
              @click=${() => this.selectCommand(cmd)}
            >
              <span class="chat-command-name">/${cmd.name}</span>
              <span class="chat-command-desc">${cmd.description}</span>
            </button>
          `
        )}
      </div>
    `;
  }

  protected override render(): TemplateResult {
    const containerClass = this.canvasVisible
      ? "chat-container chat-container--canvas"
      : "chat-container";

    const chatContent = html`
      <div class="chat-content-pane">
        ${this.messages.length > 0 || this.streamingContent ? this.renderHeader() : ""}
        <div class="chat-messages" style="position: relative;">
          <div class="chat-messages-inner">
            ${this.messages.length === 0 && !this.streamingContent
              ? this.renderEmptyState()
              : this.renderMessages()}
            ${this.toolStatus === "running" ? this.renderToolIndicator() : ""}
            ${this.streamingContent
              ? this.renderStreamingMessage()
              : this.isTyping
                ? this.renderTypingIndicator()
                : ""}
          </div>
          ${this.selectionPos && this.memoryEnabled
            ? html`
                <button
                  class="memory-save-fab"
                  style="top: ${this.selectionPos.y}px; left: ${this.selectionPos.x}px;"
                  @mousedown=${this.handleSaveToMemory}
                >
                  ${this.memorySaveStatus === "saving"
                    ? "Saving\u2026"
                    : this.memorySaveStatus === "saved"
                      ? "\u2713 Saved"
                      : "Save to Memory"}
                </button>
              `
            : ""}
        </div>
        ${this.renderSubAgentPanel()}
        ${this.forkMode
          ? html`
              <div class="fork-mode-banner">
                <span>Click "Fork from here" on any assistant message to create a new conversation branch</span>
                <button class="btn btn--ghost btn--sm" @click=${this.handleExitForkMode}>Cancel</button>
              </div>
            `
          : this.renderInput()}
        ${this.renderLightbox()}
      </div>
    `;

    return html`
      <div class=${containerClass}>
        ${chatContent}
        ${this.canvasVisible
          ? html`<katanoid-canvas-panel
              @canvas-close=${this.handleCanvasClose}
            ></katanoid-canvas-panel>`
          : ""}
      </div>
    `;
  }

  private handleCanvasClose(): void {
    this.dispatchEvent(
      new CustomEvent("canvas-close", { bubbles: true, composed: true })
    );
  }

  private renderSubAgentPanel(): TemplateResult | string {
    if (this.subAgentRuns.length === 0) return "";

    const active = this.subAgentRuns.filter(
      (r) => r.status === "pending" || r.status === "running"
    );
    const completed = this.subAgentRuns.filter(
      (r) => r.status === "completed" || r.status === "failed" || r.status === "cancelled"
    );

    return html`
      <div class="sub-agents-panel">
        <div class="sub-agents-header">
          <span class="sub-agents-title">
            Background Tasks
            ${active.length > 0
              ? html`<span class="sub-agents-count">${active.length} active</span>`
              : ""}
          </span>
        </div>
        <div class="sub-agents-list">
          ${active.map((run) => this.renderSubAgentCard(run))}
          ${completed.map((run) => this.renderSubAgentCard(run))}
        </div>
      </div>
    `;
  }

  private renderSubAgentCard(run: SubAgentRun): TemplateResult {
    const isActive = run.status === "pending" || run.status === "running";
    const isFailed = run.status === "failed";
    const isCancelled = run.status === "cancelled";

    const statusIcon = isActive
      ? html`<span class="spinner spinner--sm"></span>`
      : isFailed
        ? html`<span class="sub-agent-icon sub-agent-icon--error">!</span>`
        : isCancelled
          ? html`<span class="sub-agent-icon sub-agent-icon--cancelled">-</span>`
          : html`<span class="sub-agent-icon sub-agent-icon--done">&#10003;</span>`;

    const elapsed = run.startedAt
      ? this.formatElapsed(new Date(run.startedAt).getTime())
      : "";

    return html`
      <div class="sub-agent-card sub-agent-card--${run.status}">
        <div class="sub-agent-card-header">
          ${statusIcon}
          <span class="sub-agent-label">${run.label ?? "Task"}</span>
          ${elapsed ? html`<span class="sub-agent-elapsed">${elapsed}</span>` : ""}
          ${isActive
            ? html`
                <button
                  class="btn btn--ghost btn--xs sub-agent-cancel"
                  @click=${() => this.handleCancelSubAgent(run.id)}
                  title="Cancel"
                >&times;</button>
              `
            : ""}
        </div>
        ${run.status === "completed" && run.resultSummary
          ? html`<div class="sub-agent-result">${run.resultSummary}</div>`
          : ""}
        ${isFailed && run.error
          ? html`<div class="sub-agent-error">${run.error}</div>`
          : ""}
        ${run.creditsUsed > 0
          ? html`<span class="sub-agent-credits">${run.creditsUsed} credits</span>`
          : ""}
      </div>
    `;
  }

  private handleCancelSubAgent(runId: string): void {
    this.dispatchEvent(
      new CustomEvent("cancel-sub-agent", {
        detail: { runId },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleForkFromHere(messageId: string): void {
    this.dispatchEvent(
      new CustomEvent("fork-session", {
        detail: { messageId },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleExitForkMode = (): void => {
    this.dispatchEvent(
      new CustomEvent("exit-fork-mode", { bubbles: true, composed: true })
    );
  };

  private formatElapsed(startMs: number): string {
    const diff = Date.now() - startMs;
    if (diff < 1000) return "<1s";
    if (diff < 60000) return `${Math.floor(diff / 1000)}s`;
    return `${Math.floor(diff / 60000)}m ${Math.floor((diff % 60000) / 1000)}s`;
  }

  private renderHeader(): TemplateResult {
    return html`
      <div class="chat-header">
        <div class="chat-header-title">${this.assistantName}</div>
        <button
          class="btn btn--ghost btn--sm"
          @click=${() => {
            this.dispatchEvent(
              new CustomEvent("new-session", { bubbles: true, composed: true })
            );
          }}
          title="New conversation"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          New Chat
        </button>
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <div class="chat-empty">
        <div class="chat-empty-logo">
          <svg viewBox="0 0 48 48" fill="none">
            <path d="M24 4L6 14v20l18 10 18-10V14L24 4z" fill="white" opacity="0.9"/>
            <path d="M24 4L6 14l18 10 18-10L24 4z" fill="white"/>
          </svg>
        </div>

        <div>
          <h2 class="chat-empty-title">One assistant, every platform.</h2>
          <p class="chat-empty-subtitle">
            Chat here, or connect your favorite messaging apps to talk from anywhere.
          </p>
        </div>

        <div class="chat-empty-channels">
          ${PLATFORM_LIST.map(
            (p) => html`
              <div class="chat-empty-channel-icon" title=${p.name} .innerHTML=${p.svg}>
              </div>
            `
          )}
        </div>

        <div class="chat-empty-actions">
          <button
            class="btn btn--primary"
            @click=${() => {
              const input = this.querySelector(".chat-input") as HTMLTextAreaElement | null;
              input?.focus();
            }}
          >
            Start chatting
          </button>
          <button
            class="btn btn--secondary"
            @click=${() => {
              window.location.hash = "channels";
            }}
          >
            Connect a channel
          </button>
        </div>

        ${this.gatewayState !== "connected"
          ? html`
              <div class="model-badge">
                ${this.gatewayState === "connecting"
                  ? html`<span class="spinner spinner--sm"></span> Connecting...`
                  : "Disconnected"}
              </div>
            `
          : ""}
      </div>
    `;
  }

  private renderMessages(): TemplateResult[] {
    const result: TemplateResult[] = [];
    let lastDateLabel = "";

    for (let i = 0; i < this.messages.length; i++) {
      const msg = this.messages[i];
      const isUser = msg.role === "user";
      const prev = i > 0 ? this.messages[i - 1] : null;
      const next = i < this.messages.length - 1 ? this.messages[i + 1] : null;
      const isConsecutive = prev?.role === msg.role;
      const nextIsSameRole = next?.role === msg.role;

      // Time separator
      if (msg.timestamp) {
        const dateLabel = getDateLabel(msg.timestamp);
        if (dateLabel !== lastDateLabel) {
          lastDateLabel = dateLabel;
          result.push(html`
            <div class="time-separator">
              <span class="time-separator-text">${dateLabel}</span>
            </div>
          `);
        }
      }

      // Determine consecutive class
      let consecutiveClass = "";
      if (isConsecutive && nextIsSameRole) {
        consecutiveClass = "message-row--consecutive";
      } else if (isConsecutive && !nextIsSameRole) {
        consecutiveClass = "message-row--consecutive-last";
      }

      // For assistant messages, extract any embedded audio/image data URIs
      // and also include persisted TTS audio + screenshots from metadata
      let displayContent = msg.content;
      let audioUrls: string[] = [];
      let imageUrls: string[] = [];
      if (msg.role === "assistant") {
        const audioExtracted = extractAudioUrls(msg.content);
        const imageExtracted = extractImageUrls(audioExtracted.cleanContent);
        displayContent = imageExtracted.cleanContent;
        audioUrls = audioExtracted.audioUrls;
        imageUrls = imageExtracted.imageUrls;

        // Include persisted TTS audio from metadata (historical messages)
        if (msg.ttsAudio) {
          for (const audio of msg.ttsAudio) {
            if (audio.signedUrl) {
              audioUrls.push(audio.signedUrl);
            }
          }
        }

        // Include persisted screenshots from metadata (historical messages)
        if (msg.screenshots) {
          for (const ss of msg.screenshots) {
            if (ss.signedUrl) {
              imageUrls.push(ss.signedUrl);
            } else {
              // File expired or deleted — push sentinel
              imageUrls.push("expired");
            }
          }
        }
      }

      result.push(html`
        <div class="message-row message-row--${msg.role} ${consecutiveClass}">
          <div class="message-card message-card--${msg.role}">
            ${msg.role === "assistant" ? unsafeHTML(renderMarkdown(displayContent)) : msg.content}
          </div>
          ${imageUrls.length > 0
            ? imageUrls.map((url) =>
                url === "expired"
                  ? html`<div class="screenshot-expired">Screenshot expired</div>`
                  : html`<img src=${url} alt="Screenshot" class="chat-screenshot" loading="lazy" @click=${() => this.openLightbox(url)} />`
              )
            : ""}
          ${audioUrls.length > 0
            ? audioUrls.map((url) => this.renderAudioPlayer(url))
            : ""}
          ${msg.channel && !isUser
            ? html`
                <span class="channel-badge">
                  <span
                    class="channel-badge-dot"
                    style="background: ${CHANNEL_COLORS[msg.channel] ?? "var(--text-tertiary)"}"
                  ></span>
                  via ${msg.channel}
                </span>
              `
            : ""}
          ${msg.model && !isUser
            ? html`<span class="model-badge ${msg.model.includes("flash") || msg.model.includes("haiku") ? "model-badge--free" : ""}">${msg.model}</span>`
            : ""}
          ${this.forkMode && msg.role === "assistant"
            ? html`
                <button
                  class="btn btn--ghost btn--sm fork-from-here-btn"
                  @click=${() => this.handleForkFromHere(msg.id)}
                >Fork from here</button>
              `
            : ""}
        </div>
      `);
    }

    return result;
  }

  private renderStreamingMessage(): TemplateResult {
    // Strip any audio/image data URIs from streaming content so the raw base64
    // doesn't flash as text in the chat bubble while streaming.
    const { cleanContent: afterAudio, audioUrls } = extractAudioUrls(this.streamingContent);
    const { cleanContent, imageUrls } = extractImageUrls(afterAudio);

    return html`
      <div class="message-row message-row--assistant">
        <div class="message-card message-card--assistant message-card--streaming">
          ${cleanContent ? unsafeHTML(renderMarkdown(cleanContent)) : ""}
          <span class="streaming-cursor"></span>
        </div>
        ${imageUrls.length > 0
          ? imageUrls.map((url) => html`<img src=${url} alt="Screenshot" class="chat-screenshot" loading="lazy" @click=${() => this.openLightbox(url)} />`)
          : ""}
        ${audioUrls.length > 0
          ? audioUrls.map((url) => this.renderAudioPlayer(url))
          : ""}
      </div>
    `;
  }

  /** Unique counter for audio player element IDs */
  private audioIdCounter = 0;

  private renderAudioPlayer(audioUrl: string): TemplateResult {
    const id = `audio-${++this.audioIdCounter}-${Date.now()}`;

    return html`
      <div class="audio-player-card" id=${id}>
        <audio
          preload="metadata"
          class="audio-player-hidden"
          @loadedmetadata=${(e: Event) => this.onAudioMeta(id, e)}
          @timeupdate=${(e: Event) => this.onAudioTime(id, e)}
          @ended=${() => this.onAudioEnded(id)}
        >
          <source src=${audioUrl} type="audio/mpeg" />
        </audio>

        <!-- Play / Pause -->
        <button
          class="ap-btn ap-play"
          @click=${() => this.togglePlay(id)}
          title="Play"
        >
          <svg class="ap-icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
          <svg class="ap-icon-pause" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display:none">
            <rect x="6" y="4" width="4" height="16" rx="1"/>
            <rect x="14" y="4" width="4" height="16" rx="1"/>
          </svg>
        </button>

        <!-- Time -->
        <span class="ap-time">
          <span class="ap-current">0:00</span>
          <span class="ap-sep">/</span>
          <span class="ap-duration">--:--</span>
        </span>

        <!-- Progress track -->
        <div class="ap-track" @click=${(e: MouseEvent) => this.seekAudio(id, e)}>
          <div class="ap-progress" style="width: 0%"></div>
        </div>

        <!-- Download -->
        <button
          class="ap-btn ap-download"
          @click=${() => this.downloadAudio(audioUrl)}
          title="Download audio"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
      </div>
    `;
  }

  // ── Audio player helpers ──

  private getAudioEl(id: string): HTMLAudioElement | null {
    return this.querySelector(`#${id} audio`) as HTMLAudioElement | null;
  }

  private togglePlay(id: string): void {
    const audio = this.getAudioEl(id);
    if (!audio) return;
    if (audio.paused) {
      audio.play();
      this.setPlayState(id, true);
    } else {
      audio.pause();
      this.setPlayState(id, false);
    }
  }

  private setPlayState(id: string, playing: boolean): void {
    const container = this.querySelector(`#${id}`);
    if (!container) return;
    const iconPlay = container.querySelector(".ap-icon-play") as HTMLElement;
    const iconPause = container.querySelector(".ap-icon-pause") as HTMLElement;
    if (iconPlay) iconPlay.style.display = playing ? "none" : "";
    if (iconPause) iconPause.style.display = playing ? "" : "none";
  }

  private onAudioMeta(id: string, e: Event): void {
    const audio = e.target as HTMLAudioElement;
    const container = this.querySelector(`#${id}`);
    if (!container) return;
    const dur = container.querySelector(".ap-duration");
    if (dur) dur.textContent = this.fmtTime(audio.duration);
  }

  private onAudioTime(id: string, e: Event): void {
    const audio = e.target as HTMLAudioElement;
    const container = this.querySelector(`#${id}`);
    if (!container) return;
    const cur = container.querySelector(".ap-current");
    const bar = container.querySelector(".ap-progress") as HTMLElement;
    if (cur) cur.textContent = this.fmtTime(audio.currentTime);
    if (bar && audio.duration) {
      bar.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
    }
  }

  private onAudioEnded(id: string): void {
    this.setPlayState(id, false);
  }

  private seekAudio(id: string, e: MouseEvent): void {
    const audio = this.getAudioEl(id);
    if (!audio || !audio.duration) return;
    const track = e.currentTarget as HTMLElement;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  }

  private fmtTime(s: number): string {
    if (!isFinite(s)) return "--:--";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  }

  private downloadAudio(audioUrl: string): void {
    try {
      // Handle HTTP URLs (signed URLs from storage)
      if (audioUrl.startsWith("http")) {
        const a = document.createElement("a");
        a.href = audioUrl;
        a.download = `speech-${Date.now()}.mp3`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      // Handle data URIs (live streaming audio)
      const parts = audioUrl.split(",");
      const mime = parts[0].match(/:(.*?);/)?.[1] ?? "audio/mpeg";
      const raw = atob(parts[1]);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) {
        arr[i] = raw.charCodeAt(i);
      }
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `speech-${Date.now()}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("[Chat] Failed to download audio:", err);
    }
  }

  private renderToolIndicator(): TemplateResult {
    const label = this.getToolLabel();
    return html`
      <div class="message-row message-row--assistant">
        <div class="tool-indicator">
          <span class="spinner spinner--sm"></span>
          <span class="tool-indicator-text">${label}</span>
        </div>
      </div>
    `;
  }

  private getToolLabel(): string {
    if (this.activeTools.length === 0) return "Using tools...";
    if (this.activeTools.includes("web_search")) return "Searching the web...";
    if (this.activeTools.includes("web_fetch")) return "Reading page...";
    if (this.activeTools.includes("image_analyze")) return "Analyzing image...";
    if (this.activeTools.includes("tts")) return "Generating speech...";
    if (this.activeTools.includes("memory_search")) return "Searching memory...";
    if (this.activeTools.includes("memory_save")) return "Saving to memory...";
    if (this.activeTools.includes("device_browser")) return "Controlling browser...";
    if (this.activeTools.includes("device_terminal")) return "Running command...";
    if (this.activeTools.includes("device_filesystem")) return "Accessing files...";
    if (this.activeTools.includes("device_screen_capture")) return "Capturing screen...";
    if (this.activeTools.includes("device_clipboard")) return "Accessing clipboard...";
    if (this.activeTools.includes("device_system_info")) return "Getting system info...";
    if (this.activeTools.includes("device_notification")) return "Sending notification...";
    if (this.activeTools.includes("device_search")) return "Searching files...";
    if (this.activeTools.includes("device_apps")) return "Managing apps...";
    if (this.activeTools.includes("device_file_watcher")) return "Watching files...";
    if (this.activeTools.includes("spawn_sub_agent")) return "Spawning sub-agent...";
    if (this.activeTools.includes("canvas")) return "Updating canvas...";
    // Check for skill tools (skill__<slug>__<action_id>)
    const skillTool = this.activeTools.find((t) => t.startsWith("skill__"));
    if (skillTool) {
      const parts = skillTool.split("__");
      const slug = parts[1] ?? "skill";
      return `Running ${slug}...`;
    }
    return "Using tools...";
  }

  private renderTypingIndicator(): TemplateResult {
    return html`
      <div class="message-row message-row--assistant">
        <div class="message-card message-card--assistant">
          <div class="typing-indicator">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
          </div>
        </div>
      </div>
    `;
  }

  private renderInput(): TemplateResult {
    const currentTier = TIERS.find((t) => t.id === this.conversationMode) ?? TIERS[2]; // default Normal
    const currentModelId = getModelForTier(currentTier.id);
    const currentModel = getModelById(currentModelId);

    const selectMode = (id: ConversationMode) => {
      this.modePopoverOpen = false;
      this.dispatchEvent(
        new CustomEvent("mode-change", {
          detail: { mode: id },
          bubbles: true,
          composed: true,
        })
      );
    };

    return html`
      <div class="chat-input-container" style="position: relative;">
        ${this.commandPickerOpen ? this.renderCommandPicker() : ""}
        <div class="chat-input-wrapper">
          ${this.pendingImage
            ? html`
                <div class="chat-image-preview">
                  <img src=${this.pendingImage} alt="Attached image" />
                  <button
                    class="chat-image-preview-remove"
                    @click=${this.removeImage}
                    title="Remove image"
                  >&times;</button>
                </div>
              `
            : ""}
          <textarea
            class="chat-input"
            placeholder=${this.voiceState === "listening" && this.interimTranscript
              ? this.interimTranscript
              : `Message ${this.assistantName}...`}
            rows="1"
            .value=${this.inputValue}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
          ></textarea>
          <input
            type="file"
            accept="image/*"
            class="chat-file-input"
            style="display: none;"
            @change=${this.handleFileSelect}
          />
          <button
            class="chat-attach-btn"
            @click=${this.triggerFileInput}
            title="Attach image"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </button>
          ${SpeechRecognitionService.isSupported() ? html`
            <button
              class="chat-mic-btn ${this.voiceState === "listening" ? "chat-mic-btn--active" : ""}"
              @click=${this.handleMicToggle}
              @mousedown=${this.handleMicDown}
              @mouseup=${this.handleMicUp}
              title=${this.voiceState === "listening" ? "Stop listening" : "Voice input"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
          ` : ""}
          <div class="chat-mode-anchor">
            <button
              class="chat-mode-chip"
              @click=${(e: Event) => { e.stopPropagation(); this.modePopoverOpen = !this.modePopoverOpen; }}
              title="Change response mode"
            >
              ${currentTier.label}
              <span class="chat-mode-mult">${currentTier.mult}</span>
              <svg class="chat-mode-chevron ${this.modePopoverOpen ? "chat-mode-chevron--open" : ""}" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            ${this.modePopoverOpen ? html`
              <div class="chat-mode-popover">
                ${TIERS.map((t) => {
                  const modelId = getModelForTier(t.id);
                  const model = getModelById(modelId);
                  return html`
                    <button
                      class="chat-mode-option ${t.id === this.conversationMode ? "chat-mode-option--active" : ""}"
                      @click=${() => selectMode(t.id)}
                    >
                      <span class="chat-mode-option-label">${t.label}</span>
                      <span class="chat-mode-option-model">${model?.label ?? ""}</span>
                      <span class="chat-mode-option-mult">${t.mult}</span>
                    </button>
                  `;
                })}
              </div>
            ` : ""}
          </div>
          <button
            class="chat-send-btn"
            ?disabled=${(!this.inputValue.trim() && !this.pendingImage) || this.gatewayState !== "connected"}
            @click=${this.handleSend}
          >
            <svg viewBox="0 0 24 24"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>
          </button>
        </div>
      </div>
    `;
  }

  private handleInput(e: InputEvent): void {
    const textarea = e.target as HTMLTextAreaElement;
    this.inputValue = textarea.value;

    // Auto-resize
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";

    // Slash command detection
    if (this.inputValue.startsWith("/") && this.skillCommands.length > 0) {
      this.commandPickerOpen = true;
      this.commandPickerIndex = 0;
    } else {
      this.commandPickerOpen = false;
    }
  }

  /** Get filtered commands matching the current input */
  private getFilteredCommands(): SkillSlashCommand[] {
    if (!this.inputValue.startsWith("/")) return [];
    const query = this.inputValue.slice(1).toLowerCase().split(/\s/)[0];
    if (!query) return this.skillCommands;
    return this.skillCommands.filter(
      (cmd) =>
        cmd.name.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query)
    );
  }

  /** Select a slash command from the picker */
  private selectCommand(cmd: SkillSlashCommand): void {
    // Extract any text after the command name
    const parts = this.inputValue.match(/^\/\S*\s*(.*)/);
    const remainder = parts?.[1] ?? "";
    this.inputValue = cmd.prepend_prompt + remainder;
    this.commandPickerOpen = false;

    // Focus and position cursor at end
    const textarea = this.querySelector(".chat-input") as HTMLTextAreaElement | null;
    if (textarea) {
      textarea.value = this.inputValue;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      textarea.focus();
      textarea.setSelectionRange(this.inputValue.length, this.inputValue.length);
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    // Handle command picker navigation
    if (this.commandPickerOpen) {
      const filtered = this.getFilteredCommands();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.commandPickerIndex = Math.min(this.commandPickerIndex + 1, filtered.length - 1);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.commandPickerIndex = Math.max(this.commandPickerIndex - 1, 0);
        return;
      }
      if (e.key === "Enter" && filtered.length > 0) {
        e.preventDefault();
        this.selectCommand(filtered[this.commandPickerIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        this.commandPickerOpen = false;
        return;
      }
      if (e.key === "Tab" && filtered.length > 0) {
        e.preventDefault();
        this.selectCommand(filtered[this.commandPickerIndex]);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  private triggerFileInput(): void {
    const fileInput = this.querySelector(".chat-file-input") as HTMLInputElement | null;
    fileInput?.click();
  }

  private handleFileSelect(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      return;
    }

    // Max 5 MB
    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.pendingImage = reader.result as string;
    };
    reader.readAsDataURL(file);

    // Reset file input so the same file can be re-selected
    input.value = "";
  }

  private removeImage(): void {
    this.pendingImage = null;
  }

  // ── Voice Input Handlers ──

  private getOrCreateSpeechService(): SpeechRecognitionService {
    if (!this.speechService) {
      this.speechService = new SpeechRecognitionService({
        onResult: (transcript, isFinal) => {
          if (isFinal) {
            const prefs = loadToolPreferences();
            if (prefs.voiceMode === "hold") {
              // Auto-send in hold mode
              this.inputValue = transcript;
              this.interimTranscript = "";
              this.handleSend();
            } else {
              // In toggle mode, populate textarea for review
              this.inputValue = (this.inputValue + " " + transcript).trim();
              this.interimTranscript = "";
              const textarea = this.querySelector(".chat-input") as HTMLTextAreaElement | null;
              if (textarea) {
                textarea.style.height = "auto";
                textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
              }
            }
          } else {
            this.interimTranscript = transcript;
          }
        },
        onStateChange: (state) => {
          this.voiceState = state;
          if (state === "idle") {
            this.interimTranscript = "";
          }
        },
        onError: (error) => {
          console.warn("[Voice]", error);
          this.interimTranscript = "";
        },
      });
    }
    return this.speechService;
  }

  private handleMicToggle = (): void => {
    const prefs = loadToolPreferences();
    if (prefs.voiceMode === "hold") return; // hold mode uses mousedown/up

    const svc = this.getOrCreateSpeechService();
    if (this.voiceState === "listening") {
      svc.stop();
    } else {
      svc.start(true); // continuous for toggle
    }
  };

  private handleMicDown = (): void => {
    const prefs = loadToolPreferences();
    if (prefs.voiceMode !== "hold") return;

    const svc = this.getOrCreateSpeechService();
    svc.start(false); // non-continuous for hold
  };

  private handleMicUp = (): void => {
    const prefs = loadToolPreferences();
    if (prefs.voiceMode !== "hold") return;

    const svc = this.getOrCreateSpeechService();
    svc.stop();
  };

  private handleSend(): void {
    if (!this.inputValue.trim() && !this.pendingImage) return;

    // If an image is attached, prepend a reference the AI can use
    let content = this.inputValue.trim();
    const imageDataUri = this.pendingImage;

    if (imageDataUri && !content) {
      content = "What's in this image?";
    }

    this.dispatchEvent(
      new CustomEvent("send-message", {
        detail: { content, imageDataUri },
        bubbles: true,
        composed: true,
      })
    );

    this.inputValue = "";
    this.pendingImage = null;
    const textarea = this.querySelector(".chat-input") as HTMLTextAreaElement | null;
    if (textarea) {
      textarea.style.height = "auto";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-chat-view": KatanoidChatView;
  }
}
